import React, { useEffect, useRef } from 'react';
import { chargerBwipjs, svgDuCodeBarres, SYMBOLOGIES_CARREES } from '../../utils/codeBarres';

// La recette du dessin (options bwip-js, SVG dimensionné) vit dans
// utils/codeBarres.js : l'export dessine avec la même, depuis les données du calque.
export { SYMBOLOGIES_CARREES };

// Les codes-barres en cours de dessin. La page d'impression (PrintView) les
// attend avant de se déclarer prête (`__LF_READY__`) : bwip-js arrive par un
// import dynamique, et l'<img> du code-barres n'existe pas encore quand la page
// compte ses images. Observé en production le 2026-10-08 : premier rendu d'un
// conteneur à froid SANS code-barres, rendus suivants complets. Reproduit en
// retardant le morceau bwip-js : 3 rendus sur 3 sans code-barres.
const dessinsEnCours = new Set();

const EN_RETARD = 'lf:codes-barres-en-retard';

/**
 * Résout quand chaque code-barres monté est dessiné (son image chargée), ou en
 * échec visible. Ne bloque jamais au-delà de `delaiMs` : mieux vaut une capture
 * qu'un rendu qui n'aboutit pas (le service attend 45 s au plus). Au-delà, les
 * codes-barres encore en attente affichent leur échec : une étiquette ne sort
 * JAMAIS sans code-barres sans que cela se voie.
 */
export function codesBarresDessines(delaiMs = 15000) {
    if (dessinsEnCours.size === 0) return Promise.resolve();
    let minuterie;
    return Promise.race([
        Promise.allSettled([...dessinsEnCours]).then(() => clearTimeout(minuterie)),
        new Promise((r) => {
            minuterie = setTimeout(() => { window.dispatchEvent(new Event(EN_RETARD)); r(); }, delaiMs);
        }),
    ]);
}

// Rendu d'un calque code-barres : bwip-js le dessine en SVG, affiché dans une
// <img>. VECTORIEL, et non plus un bitmap à `bcScale` pixels par module : ce
// bitmap, étiré à la taille du cadre, sortait flou à l'export (bords gris de
// 1,45 px à 300 dpi, de 3 px à 600 dpi — `banc/banc-code-barres-rendu.py`),
// au point qu'un EAN-13 n'était plus lisible (retour de Louise, 2026-10-09).
// En SVG, un bord ne coupe qu'un pixel au plus, à toute définition, et le PDF
// vectoriel garde de vrais tracés. L'<img> plutôt qu'un <canvas> visible évite
// un bug de compositing GPU de Safari dans un conteneur transformé (scale,
// rotate). Un canvas caché reste peint pour fitFrame et pour l'export SVG.
export default function BarcodeElement({ el }) {
    const canvasRef = useRef(null);
    const [imgSrc, setImgSrc] = React.useState(null);
    const [errorMsg, setErrorMsg] = React.useState(null);
    // La fin du dessin en cours : appelée quand l'<img> a chargé, ou en cas d'échec.
    const finDuDessin = useRef(null);
    const srcAffichee = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let resoudre;
        const dessin = new Promise((r) => { resoudre = r; });
        dessinsEnCours.add(dessin);
        const terminer = () => { dessinsEnCours.delete(dessin); resoudre(); };
        finDuDessin.current = terminer;
        chargerBwipjs().then((bwipjs) => {
            if (cancelled) return;
        try {
            const src = svgDuCodeBarres(bwipjs, el);
            setImgSrc(src);
            setErrorMsg(null);
            // Image identique à celle déjà affichée : React ne la recharge pas,
            // et onLoad ne viendrait jamais.
            if (src === srcAffichee.current) terminer();
        } catch (e) {
            console.warn('Barcode issue:', e);
            setErrorMsg(e.message || "Erreur de format");
            setImgSrc(null);
            terminer();
        }
        }).catch((e) => {
            // Le morceau bwip-js n'a pas pu se charger : un échec VISIBLE plutôt
            // qu'une étiquette qui sortirait sans code-barres sans que rien le dise.
            if (cancelled) return;
            console.warn('bwip-js indisponible :', e);
            setErrorMsg('Code-barres non dessiné');
            setImgSrc(null);
            terminer();
        });
        return () => { cancelled = true; terminer(); };
    }, [el.symbology, el.text, el.showText, el.color, el.bgColor, el.bcScale, el.bcHeight, el.bcMargin, el.bcInkSpread, el.size, el.font]);

    // Délai de dessin dépassé (codesBarresDessines) : l'échec doit se voir sur l'épreuve.
    useEffect(() => {
        const enRetard = () => { if (!srcAffichee.current) setErrorMsg('Code-barres non dessiné (délai dépassé)'); };
        window.addEventListener(EN_RETARD, enRetard);
        return () => window.removeEventListener(EN_RETARD, enRetard);
    }, []);

    // Canvas caché pour l'export (html2canvas en a besoin)
    useEffect(() => {
        if (canvasRef.current && imgSrc) {
            const img = new Image();
            img.onload = () => {
                canvasRef.current.width = img.width;
                canvasRef.current.height = img.height;
                const ctx = canvasRef.current.getContext('2d');
                ctx.drawImage(img, 0, 0);
            };
            img.src = imgSrc;
        }
    }, [imgSrc]);

    return (
        <div className="relative w-full h-full flex items-center justify-center border border-transparent hover:border-white/50 transition-colors" style={{ backgroundColor: el.bgColor && el.bgColor !== 'transparent' ? el.bgColor : 'transparent' }}>
            {imgSrc && (
                <img
                    src={imgSrc}
                    alt="barcode"
                    className="max-w-full max-h-full object-contain pointer-events-none"
                    onLoad={() => { srcAffichee.current = imgSrc; finDuDessin.current?.(); }}
                    onError={() => finDuDessin.current?.()}
                />
            )}
            <canvas id={`barcode-canvas-${el.id}`} ref={canvasRef} style={{ display: 'none' }} />
            {errorMsg && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/90 text-white text-[10px] font-mono text-center p-2 font-bold z-10 break-words rounded overflow-hidden">
                    <span className="text-xl mb-1">⚠️</span>
                    {errorMsg}
                </div>
            )}
        </div>
    );
}
